import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { T } from '@tolgee/react';

const NotFoundPage = () => {
  return (
    <>
      <Helmet>
        <title><T keyName="error.page_title">Página no encontrada | Jarad</T></title>
        <meta name="description" content="La página que buscas no existe. Vuelve al inicio para encontrar nuestros productos de nutrición premium para mascotas." />
      </Helmet>

      <div style={{backgroundColor: '#f2ede4'}} className="min-h-screen flex items-center justify-center">
        <div className="content-container">
          <div className="text-center">
            {/* Animated 404 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="mb-8"
            >
              <h1 
                className="text-8xl md:text-9xl font-extrabold tracking-tight mb-4"
                style={{color: '#f6cf02'}}
              >
                404
              </h1>
              <div 
                className="w-24 h-1 mx-auto rounded-full mb-8"
                style={{backgroundColor: '#0d0d0d'}}
              ></div>
            </motion.div>

            {/* Error message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-8"
            >
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{color: '#0d0d0d'}}
              >
                <T keyName="error.not_found_title">Página no encontrada</T>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                <T keyName="error.not_found_description">Lo sentimos, la página que buscas no existe o ha sido movida. Te invitamos a explorar nuestros productos de nutrición premium para mascotas.</T>
              </p>
            </motion.div>

            {/* Pet icons decoration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center space-x-8 mb-12"
            >
              {[
                { icon: '🐦', color: '#8ed1fc', name: 'birds' },
                { icon: '🐰', color: '#f6cf02', name: 'rodents' },
                { icon: '🐱', color: '#c7c7c7', name: 'cats' },
                { icon: '🐕', color: '#e8b4a0', name: 'dogs' }
              ].map((pet, index) => (
                <motion.div
                  key={pet.name}
                  className="w-16 h-16 rounded-full flex items-center justify-center text-2xl"
                  style={{backgroundColor: pet.color}}
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                >
                  {pet.icon}
                </motion.div>
              ))}
            </motion.div>

            {/* Action buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link
                to="/"
                className="px-8 py-3 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg"
                style={{
                  backgroundColor: '#f6cf02',
                  color: '#0d0d0d',
                  borderColor: '#0d0d0d'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0d0d0d';
                  e.currentTarget.style.color = '#f6cf02';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f6cf02';
                  e.currentTarget.style.color = '#0d0d0d';
                }}
              >
                <T keyName="error.back_to_home">Volver al inicio</T>
              </Link>
              
              <Link
                to="/categories"
                className="px-8 py-3 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg bg-white hover:bg-gray-50"
                style={{
                  color: '#0d0d0d',
                  borderColor: '#0d0d0d'
                }}
              >
                <T keyName="error.view_products">Ver productos</T>
              </Link>
            </motion.div>

            {/* Helpful links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-12 pt-8 border-t border-gray-300"
            >
              <p className="text-gray-600 mb-4"><T keyName="error.need_help">¿Necesitas ayuda? Prueba estos enlaces:</T></p>
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <Link
                  to="/about-us"
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <T keyName="header.about_us">Sobre nosotros</T>
                </Link>
                <Link
                  to="/brands"
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <T keyName="error.our_brands">Nuestras marcas</T>
                </Link>
                <Link
                  to="/faq"
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <T keyName="error.faq">FAQ</T>
                </Link>
                <Link
                  to="/contact"
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <T keyName="header.contact">Contacto</T>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Floating elements decoration */}
        <motion.div
          className="absolute top-20 left-10 w-8 h-8 rounded-full opacity-20"
          style={{backgroundColor: '#f6cf02'}}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-6 h-6 rounded-full opacity-20"
          style={{backgroundColor: '#8ed1fc'}}
          animate={{
            y: [0, 20, 0],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div
          className="absolute top-1/2 left-20 w-4 h-4 rounded-full opacity-20"
          style={{backgroundColor: '#e8b4a0'}}
          animate={{
            x: [0, 15, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        />
      </div>
    </>
  );
};

export default NotFoundPage;